import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import products from "../data/products";

const EMAIL = "info@byteflowinfotech.com";
const PHONE_DISPLAY = "+91 99585 98952";
const PHONE_TEL = "+919958598952";

const METHODS = [
  {
    icon: "✉️",
    label: "Sales & demos",
    primary: EMAIL,
    href: `mailto:${EMAIL}`,
    note: "We reply within one business day.",
  },
  {
    icon: "📞",
    label: "Phone",
    primary: PHONE_DISPLAY,
    href: `tel:${PHONE_TEL}`,
    note: "Mon – Sat, 9:30 AM – 6:30 PM IST",
  },
  {
    icon: "📍",
    label: "Office",
    primary: "TDI Centre, Jasola",
    note: "Okhla, New Delhi 110025",
  },
  {
    icon: "🕒",
    label: "Hours",
    primary: "Mon – Sat",
    note: "9:30 AM – 6:30 PM IST",
  },
];

const NEXT_STEPS = [
  {
    title: "We reply within one business day",
    desc: "A real person from our team, not an autoresponder.",
  },
  {
    title: "A 30-minute discovery call",
    desc: "We learn about your workflow, team size, and goals.",
  },
  {
    title: "A tailored product walkthrough",
    desc: "We demo only what’s relevant to the way you actually work.",
  },
  {
    title: "Free pilot, when applicable",
    desc: "For qualified businesses, we set up a sandbox so you can try before you buy.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  product: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    // Placeholder for real API; simulate a quick round-trip.
    setTimeout(() => {
      setStatus("success");
    }, 800);
  };

  const reset = () => {
    setForm(initialForm);
    setStatus("idle");
  };

  return (
    <>
      <section className="page-hero">
        <motion.div
          className="page-hero__inner"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <p className="eyebrow">Contact</p>
          <h1>Let’s talk about your business.</h1>
          <p className="page-hero__lead">
            Sales, demos, support, or partnerships — tell us what you need and
            we’ll get back within one business day.
          </p>
        </motion.div>
      </section>

      <section className="section">
        <div className="contact-methods">
          {METHODS.map((m) => (
            <motion.div
              key={m.label}
              className="contact-method"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4 }}
            >
              <div className="contact-method__icon" aria-hidden>{m.icon}</div>
              <div className="contact-method__label">{m.label}</div>
              {m.href ? (
                <a className="contact-method__primary" href={m.href}>
                  {m.primary}
                </a>
              ) : (
                <div className="contact-method__primary">{m.primary}</div>
              )}
              <div className="contact-method__note">{m.note}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section section--muted">
        <div className="contact-layout">
          <motion.div
            className="contact-form-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
          >
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  className="contact-success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  role="status"
                  aria-live="polite"
                >
                  <div className="contact-success__icon" aria-hidden>✓</div>
                  <h3>Thanks, {form.name.split(" ")[0] || "there"}.</h3>
                  <p>
                    We’ve received your message and will reply to{" "}
                    <strong>{form.email}</strong> within one business day. If
                    you need us sooner, give us a call at{" "}
                    <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a>.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={reset}
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  className="contact-form"
                  onSubmit={onSubmit}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  noValidate
                >
                  <div className="contact-form__header">
                    <h2>Send us a message</h2>
                    <p>
                      Fill in the form and we’ll route it to the right person
                      on our team.
                    </p>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="name">Full name *</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        value={form.name}
                        onChange={onChange}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="email">Work email *</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={form.email}
                        onChange={onChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="company">Company</label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        autoComplete="organization"
                        value={form.company}
                        onChange={onChange}
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={onChange}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="product">I’m interested in</label>
                    <select
                      id="product"
                      name="product"
                      value={form.product}
                      onChange={onChange}
                    >
                      <option value="">Select a product (optional)</option>
                      {products.map((p) => (
                        <option key={p.slug} value={p.slug}>
                          {p.name} {p.flagship ? "(Flagship)" : ""}
                        </option>
                      ))}
                      <option value="not-sure">Not sure yet — help me choose</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label htmlFor="message">How can we help? *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      required
                      value={form.message}
                      onChange={onChange}
                      placeholder="A few words about your business and what you’re trying to solve."
                    />
                  </div>

                  <div className="contact-form__footer">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Sending…" : "Send message"}
                    </button>
                    <p className="contact-form__privacy">
                      We’ll only use your details to respond to your enquiry.
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          <aside className="contact-sidebar">
            <h3>What happens next</h3>
            <ol className="next-steps">
              {NEXT_STEPS.map((step, i) => (
                <li key={step.title}>
                  <span className="next-steps__num">{i + 1}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="contact-sidebar__quick">
              <p className="contact-sidebar__label">Prefer to reach us directly?</p>
              <a href={`mailto:${EMAIL}`} className="contact-sidebar__link">
                <span aria-hidden>✉️</span> {EMAIL}
              </a>
              <a href={`tel:${PHONE_TEL}`} className="contact-sidebar__link">
                <span aria-hidden>📞</span> {PHONE_DISPLAY}
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section className="section">
        <div className="office-card">
          <div className="office-card__info">
            <p className="eyebrow">Visit us</p>
            <h2>Byteflow HQ — New Delhi</h2>
            <p>
              TDI Centre, Jasola
              <br />
              Okhla, New Delhi 110025
              <br />
              India
            </p>
            <p>
              <strong>Hours</strong>
              <br />
              Monday – Saturday, 9:30 AM – 6:30 PM IST
            </p>
            <a
              href="https://www.google.com/maps/place/TDI+Centre/@28.537375,77.286664,15z"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Open in Google Maps →
            </a>
          </div>
          <div className="office-card__map">
            <iframe
              title="Byteflow Office - TDI Centre, Jasola"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1714.768519596059!2d77.28666350912654!3d28.53737459371847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce6aaa4531a49%3A0xb56eaae0d8c90b2d!2sTDI%20Centre!5e1!3m2!1sen!2sin!4v1761801988641!5m2!1sen!2sin"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
